import { Box, Button, Tooltip, Typography, useTheme } from '@mui/material';
import ExoAvatar from '../../ExoAvatar';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../../global/theme/tokens';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { BoilingDiv } from './BoilingDiv';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckIcon from '@mui/icons-material/Check';
import StarIcon from '@mui/icons-material/Star';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { useNavigate } from 'react-router-dom';
import DateTimeLabel from '../../../label/DateTimeLabel';
import DateLabel from '../../../label/DateLabel';
import DeleteIcon from '@mui/icons-material/Delete';

export const LeadMatch = ({
  match,
  onPromote,
  onDemote,
  onRemove,
  onAdd,
  noScore,
  className,
  sx,
  apiData,
  onDeleteDeclinedLeads,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const { accessPoint } = match;

  const { accessLevel, accessCount } = accessPoint || {};

  // Sort levels ascending for convenience
  const levelsSorted =
    apiData?.data?.levels
      ?.slice()
      .sort((a, b) => a.accessLevel - b.accessLevel) || [];

  // Get next higher level object
  const nextLevelObj = levelsSorted.find(
    (level) => level.accessLevel > accessLevel
  );

  // Get next lower level object (last lower level in array)
  const prevLevelObj = [...levelsSorted]
    .reverse()
    .find((level) => level.accessLevel < accessLevel);

  const leadTypes = {
    companyLead: CompanyLead,
    personLead: PersonLead,
  };

  const Element = leadTypes[match.type];

  const buttonSx = {
    padding: '0px',
    borderRadius: '8px',
    width: '50px',
    minWidth: '50px',
    height: '50px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  };

  function getHeatMapColor(percentage) {
    // Clamp and normalize
    const pct = Math.max(0, Math.min(100, percentage)) / 100;

    const maxColor = 180; // softer than 255
    const baseLight = 60; // adds lightness to all channels

    const r = Math.round(baseLight + maxColor * pct);
    const g = baseLight; // stays constant, keeping it neutral
    const b = Math.round(baseLight + maxColor * (1 - pct));

    const toHex = (n) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  if (Element)
    return (
      <Box className="flex items-center gap-3 w-full">
        <Box className="flex flex-col lg:flex-row gap-2 py-2">
          {onPromote && accessPoint && nextLevelObj && (
            <Tooltip
              title={`${t('Promote to Level')} ${nextLevelObj.accessLevel}`}
            >
              <Button
                onClick={() => onPromote(match)}
                sx={{
                  ...buttonSx,
                  bgcolor: colors.info + '30',
                  border: `1px solid ${colors.info}80`,
                  ':hover': {
                    bgcolor: colors.info,
                    border: `1px solid ${colors.info}`,
                  },
                }}
              >
                <UpgradeIcon />
              </Button>
            </Tooltip>
          )}
          {onDemote && accessPoint && prevLevelObj && (
            <Tooltip
              title={`${t('Demote to Level')} ${prevLevelObj.accessLevel}`}
            >
              <Button
                onClick={() => onDemote(match)}
                sx={{
                  ...buttonSx,
                  bgcolor: colors.warning + '30',
                  border: `1px solid ${colors.warning}80`,
                  ':hover': {
                    bgcolor: colors.warning,
                    border: `1px solid ${colors.warning}`,
                  },
                }}
              >
                <UpgradeIcon sx={{ transform: 'rotate(180deg)' }} />
              </Button>
            </Tooltip>
          )}
        </Box>
        {onAdd && (
          <Tooltip title={`${t('add to sales level')} ${0}`}>
            <Button
              onClick={() => onAdd(match)}
              sx={{
                ...buttonSx,
                bgcolor: colors.success + '30',
                border: `1px solid ${colors.success}80`,
                ':hover': {
                  bgcolor: colors.success,
                  border: `1px solid ${colors.success}`,
                },
              }}
            >
              <CheckIcon />
            </Button>
          </Tooltip>
        )}
        <Box
          className={className || 'flex w-full lg:gap-4 items-center'}
          sx={sx}
        >
          <Element
            data={match.element}
            level={accessLevel}
            apiData={apiData}
            levelUpdatedAt={match.accessPoint?.levelUpdatedAt}
          />
          {accessPoint && (
            <Box className="flex flex-row-reverse gap-2 lg:flex-col">
              <Typography
                variant="h4"
                fontWeight={500}
                className="flex items-center gap-2 justify-end"
              >
                <RemoveRedEyeIcon />
                {accessCount}
              </Typography>
              <Typography textAlign="right" className="opacity-50">
                {t('views')}
              </Typography>
            </Box>
          )}
          {!noScore && match.score !== null && (
            <Box className="flex flex-row-reverse gap-2 lg:flex-col">
              <Typography
                variant="h4"
                textAlign="right"
                fontWeight={600}
                sx={{ color: getHeatMapColor(match.score) }}
                className="flex items-center gap-2"
              >
                <BoilingDiv heat={match.score}>
                  <LocalFireDepartmentIcon
                    sx={{
                      color: getHeatMapColor(match.score),
                      transform: `scale(${(match.score + 100) / 170})`,
                      opacity: match.score < 20 ? 0 : 1,
                    }}
                  />
                </BoilingDiv>
                {match.score}%
              </Typography>
              <Typography textAlign="right" className="opacity-50">
                {t('matching')}
              </Typography>
            </Box>
          )}
        </Box>
        {onDeleteDeclinedLeads && (
          <Button
            onClick={() => onDeleteDeclinedLeads(match)}
            sx={{
              ...buttonSx,
              bgcolor: colors.error + '30',
              border: `1px solid ${colors.error}80`,
              ':hover': {
                bgcolor: colors.error,
                border: `1px solid ${colors.error}`,
              },
            }}
          >
            <DeleteIcon />
          </Button>
        )}

        {onRemove && (
          <Tooltip title={t('Remove')}>
            <Button
              onClick={() => onRemove(match)}
              sx={{
                ...buttonSx,
                bgcolor: colors.error + '30',
                border: `1px solid ${colors.error}80`,
                ':hover': {
                  bgcolor: colors.error,
                  border: `1px solid ${colors.error}`,
                },
              }}
            >
              <RemoveIcon />
            </Button>
          </Tooltip>
        )}
      </Box>
    );
};

const CompanyLead = ({ data, level, apiData, levelUpdatedAt }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { name, nameShort, logo, address } = data;

  const addressString = address
    ? `${address.street} ${address.houseNumber} - ${address.zipCode} ${address.city}`
    : '';

  const handleClick = () => {
    if (data) navigate(`/leads/companies/${data.id}`);
  };

  return (
    <Box className="flex items-center gap-2 w-full">
      <ExoAvatar
        picture={logo?.temporaryUrl}
        name={nameShort || name}
        size="40px"
        onClick={handleClick}
      />
      <Box>
        <Typography fontWeight={600} className="flex items-center gap-2">
          {nameShort || name}
          <AccessLevel level={level} apiData={apiData} />
        </Typography>
        <Typography className="opacity-50">{addressString}</Typography>

        {levelUpdatedAt && (
          <Tooltip title={t('On this level since')}>
            <Typography className="opacity-50" fontSize={12}>
              <DateTimeLabel dateTime={levelUpdatedAt} />
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const PersonLead = ({ data, level, apiData, levelUpdatedAt }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { firstName, lastName, salutation, address } = data;

  const name = `${firstName || ''} ${lastName}`;

  const addressString = address
    ? `${address.street} ${address.houseNumber} - ${address.zipCode} ${address.city}`
    : '';

  const handleClick = () => {
    if (data) navigate(`/leads/persons/${data.id}`);
  };

  return (
    <Box className="flex items-center gap-2 w-full">
      <ExoAvatar name={name} size="40px" onClick={handleClick} />
      <Box>
        <Typography fontWeight={600} className="flex items-center gap-2">
          {`${salutation} ${name}`}
          <AccessLevel level={level} apiData={apiData} />
        </Typography>
        <Typography className="opacity-50">{addressString}</Typography>

        {levelUpdatedAt && (
          <Tooltip title={t('On this level since')}>
            <Typography className="opacity-50" fontSize={12}>
              <DateTimeLabel dateTime={levelUpdatedAt} />
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const AccessLevel = ({ level = 0, size, apiData }) => {
  if (level < 0) return;
  const levels = apiData?.data?.levels || [];

  // console.log('Getting levels', levels);

  const levelObj = levels.find((l) => l.accessLevel === level);

  // console.log('Levelobj', level);

  return (
    <Box className="flex items-center gap-0">
      {' - '}
      {levelObj ? levelObj.title : `Level ${level}`}
    </Box>
  );
};
